import turfBbox from '@turf/bbox'
import bboxPolygon from '@turf/bbox-polygon'
import turfCenter from '@turf/center'
import { IMap } from 'fifa-gis-library'
import { provide, type Ref, ref, shallowRef } from 'vue'

import {
  dropoffIconFactory,
  iconFactory,
  pickupIconFactory,
  waypointIconFactory,
  widerWaypointIconFactory,
} from './factories/icon-factories'
import { fifaGisMapLoadedSymbol, fifaGisMapSymbol } from './injections-keys'

export const FIFA_HEADQUARTERS_LAT_LNG = { lat: 47.38159973416059, lng: 8.57449358650673 }
export * from './factories/icon-factories'

export function useFifaGisMap(mapRef?: Ref<HTMLElement>) {
  const isMapLoaded = ref(false)
  const map = shallowRef(null)

  if (mapRef) {
    // TODO: Remove legacy provide when all components are migrated to symbols
    provide('fifaGisMap', map)
    provide(fifaGisMapSymbol, map)
    provide('fifaGisMapLoaded', isMapLoaded)
    provide(fifaGisMapLoadedSymbol, isMapLoaded)
  }

  function initializeMap(options) {
    let onLoaded
    const promise = new Promise(resolve => {
      onLoaded = resolve
    })

    let center
    if (options?.item?.type == 'Polygon') {
      const bbox = turfBbox(options.item)
      center = turfCenter(bboxPolygon(bbox)).geometry.coordinates
      center = { lat: center[0], lng: center[1] }
    } else if (options?.item?.type == 'Point') {
      center = { lat: options.item.coordinates[0], lng: options.item.coordinates[1] }
    } else if (options?.item?.latitude && options?.item?.longitude) {
      center = { lat: options.item.latitude, lng: options.item.longitude }
    }

    if (!mapRef?.value) {
      throw new Error('"mapRef" is not defined. Cannot initialize fifa gis map.')
    }

    map.value = new IMap(mapRef.value, {
      center: center || FIFA_HEADQUARTERS_LAT_LNG,
      zoom: center ? 15 : 2,
      minZoom: 1,
      maxZoom: 19,
      ...(options || {}),
    })

    map.value.whenReady(() => {
      onLoaded(map.value)
      isMapLoaded.value = true
    })

    return promise
  }

  function preparePoint(item) {
    return {
      position: {
        lat: item.coordinates[0],
        lng: item.coordinates[1],
      },
      label: item.label,
      labelOffset: [50, 50],
      labelStyle: {
        fontColor: '#000000',
        fontSize: 12,
        fontWeight: 500,
      },
      infoPopoverContent: item.title,
      infoPopoverTrigger: 'hover',
      // due to fifa gis bug, need to break the reference with shallow copy:
      // duplicated html for icons when using the same icon object
      icon: { ...item.icon } || iconFactory(item),
    }
  }

  const preparePolygon = (item, style = {}) => ({
    geometry: { ...item },
    style: {
      strokeColor: item.strokeColor || '#13c2c2',
      strokeOpacity: item.strokeOpacity || 1.0,
      strokeWeight: item.strokeWeight || 2,
      fillColor: item.fillColor || '#13c2c2',
      fillOpacity: item.fillOpacity || 0.1,
      ...style,
    },
  })

  const preparePolyline = item => ({
    geometry: { ...item },
    style: {
      strokeColor: item.strokeColor || '#326295',
      strokeOpacity: item.strokeOpacity || 0.7,
      lineWidth: item.lineWidth || 4,
      ...(item?.style || {}),
    },
  })

  const addMapItems = items => {
    items?.forEach(item => addMapItem(item))
  }

  const addMapItem = item => {
    switch (item.type) {
      case 'Polygon':
        return map.value.addPolygon(preparePolygon(item))
      case 'Point': {
        return map.value.addMarker(preparePoint(item))
      }
    }
  }

  function centerMapOn(item, options) {
    options = options || {}
    if (!item) return

    if (item.type == 'Polygon') {
      const bbox = turfBbox(item)
      const bounds = {
        ne: { lat: parseFloat(bbox[2]), lng: parseFloat(bbox[3]) },
        sw: { lat: parseFloat(bbox[0]), lng: parseFloat(bbox[1]) },
      }

      map.value.fitBounds(bounds, { padding: [20, 20], duration: 0.2, ...options })
    } else if (item.type == 'Point') {
      map.value?.panTo({
        center: { lat: item.coordinates?.[0], lng: item.coordinates?.[1] },
        zoom: 15,
        duration: 0.2,
        ...options,
      })
    } else if (item.latitude && item.longitude) {
      map.value?.panTo({
        center: { lat: item.latitude, lng: item.longitude },
        zoom: 15,
        duration: 0.2,
        ...options,
      })
    }
  }

  return {
    addMapItems,
    centerMapOn,
    initializeMap,
    isMapLoaded,
    map,
    preparePolygon,
    preparePoint,
    preparePolyline,
    iconFactory,
    waypointIconFactory,
    pickupIconFactory,
    dropoffIconFactory,
    widerWaypointIconFactory,
  }
}
